import CustomReusableDownloadable from "@bevco/Components/CustomReusableDownloadable";
import { CustomState } from "@bevco/Store/Reducers";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { FC, useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
}

/* Verndale Custom Feature - New variables from redux state */
const mapStateToProps = (state: CustomState) => ({
    invoiceTypeList: state.customSystemList["InvoiceTypes"] ?? [],
    hideDownloadDocumentButtons: (state.context.settings.settingsCollection as any).myAccountSettings
        ?.hideDownloadDocumentButtons,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface InvoiceDetailHeaderStyles {
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    headerGridContainer?: GridContainerProps;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: GridItemProps;
    printButton?: ButtonPresentationProps;
    buttonGridItem?: GridItemProps;
    printClickable?: ClickableProps;
    emailClickable?: ClickableProps;
}

export const headerStyles: InvoiceDetailHeaderStyles = {
    shareEntityButtonStyles: {
        button: {
            buttonType: "outline",
            variant: "secondary",
        },
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            margin-left: 10px;
        `,
    },
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: [1, 1, 1, 6, 6],
    },
    titleGridItem: {
        width: [11, 11, 11, 6, 6],
    },
    title: {
        variant: "h2",
        as: "h1",
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
};

const styles = headerStyles;
const InvoiceDetailHeader: FC<Props> = ({ fields, invoiceTypeList, hideDownloadDocumentButtons }) => {
    const { value: invoice } = useContext(InvoiceStateContext);
    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;
    if (!invoice) {
        return null;
    }

    const printLabel = translate("Print");

    /* Verndale Custom Feature - Get label from invoice type */
    const showInvoiceTypeLabel = () => {
        const typeFromList = invoiceTypeList.find(item => item.name === invoice.invoiceType);
        if (typeFromList) {
            return typeFromList.description ?? "";
        }
        return invoice.invoiceType;
    };

    return (
        <GridContainer {...styles.headerGridContainer}>
            <GridItem
                {...styles.titleGridItem}
                width={
                    hideDownloadDocumentButtons ? ([12, 12, 12, 12, 12] as const) : styles.titleGridItem.width
                }
            >
                {/* Verndale Custom Feature - Change translation */}
                <Typography {...styles.title}>{`${translate("{0} #", showInvoiceTypeLabel())} ${
                    invoice.invoiceNumber
                }`}</Typography>
            </GridItem>
            {!hideDownloadDocumentButtons && (
                <GridItem {...styles.buttonGridItem}>
                    {/* Verndale Custom Feature - Add reusable downloadable component */}
                    <CustomReusableDownloadable
                        hideIcon={true}
                        showAsButton={true}
                        documentId={invoice.properties["originalId"]}
                        type={showInvoiceTypeLabel()}
                    />

                    {/* Verndale Custom Feature - Hide email and print buttons */}
                {/* <Hidden {...styles.menuHiddenContainer}>
                    <OverflowMenu hasChildPortal position="end" {...styles.narrowOverflowMenu}>
                        <ShareEntityButton
                            entityId={invoice.invoiceNumber}
                            variant="clickable"
                            entityName="Invoice"
                            extendedStyles={styles.shareEntityButtonStyles}
                            generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                        />
                        <Clickable
                            {...styles.printClickable}
                            onClick={openPrintDialog}
                            data-test-selector="invoiceDetails_print"
                        >
                            {printLabel}
                        </Clickable>
                    </OverflowMenu>
                </Hidden> */}

                {/* <Hidden {...styles.buttonsHiddenContainer}>
                    <ShareEntityButton
                        entityId={invoice.invoiceNumber}
                        entityName="Invoice"
                        extendedStyles={styles.shareEntityButtonStyles}
                        generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                    />
                    <Button {...styles.printButton} onClick={openPrintDialog} data-test-selector="invoiceDetails_print">
                        {printLabel}
                    </Button>
                </Hidden> */}
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(InvoiceDetailHeader as any) as any,
    definition: {
        displayName: "Page Header",
        allowedContexts: ["InvoiceDetailsPage"],
        group: "Invoice Details",
        fieldDefinitions: [
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the invoice generates an email attachment using the Invoice Details page, rather than a pre-defined PDF file.",
            },
        ],
    },
};

export default widgetModule;
